/**
 * SPDX-License-Identifier: MPL-2.0
 * Copyright 2023-2024 (c) Marc Fischer, ISW University of Stuttgart (for umati and VDW e.V.)
 */
import React, { ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { Toast } from 'react-bootstrap';

import globalAxios, { AxiosError } from 'axios';

export interface State {
  msg: string;
  type: 'OK' | 'ERROR';
  id: number;
  time: Date;
}

interface IStatesContextType {
  states: State[];
  addState: (msg: string, type: 'OK' | 'ERROR') => void;
}
export const StatesContext = React.createContext<IStatesContextType>({} as IStatesContextType);
export function useStates() {
  return useContext(StatesContext);
}
export const StatesConsumer = StatesContext.Consumer;
export const StatesProvider = ({ children }: { children: ReactNode }) => {
  const [states, setStates] = useState<State[]>([]);

  function addState(msg: string, type: 'OK' | 'ERROR') {
    setStates((old) => {
      const list = [...old];
      list.push({ msg: msg, type: type, id: Math.random(), time: new Date() });
      // console.log(list)
      return list;
    });
  }

  useEffect(() => {
    globalAxios.interceptors.response.use(
      (response) => {
        const path = new URL(response.config?.url || '').pathname;
        const op = response.config?.method?.toUpperCase();
        const msg = `Ok: ${op} ${path}`;
        addState(msg, 'OK');
        return response;
      },
      (error: AxiosError) => {
        const path = new URL(error.config?.url || '').pathname;
        const op = error.config?.method?.toUpperCase();
        const msg = `Error: Could not ${op} ${path} ${error.message}`;
        addState(msg, 'ERROR');
        return Promise.reject(error);
      },
    );
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setStates((old) => {
        const list = [...old].filter((m) => {
          return new Date().valueOf() - m.time.valueOf() < 5000;
        });
        return list;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const memoedValue = useMemo(() => ({ states, addState }), [states]);

  return <StatesContext.Provider value={memoedValue}> {children} </StatesContext.Provider>;
};

const SingleState = (props: { state: State }): React.JSX.Element => {
  // const [time, setTime] = useState<number>(0);
  // useEffect(() => {
  //     let interval = setInterval(() => {
  //         setTime(elapsed(props.state));
  //     }, 800);
  //     return () => clearInterval(interval);
  // }, [])
  return (
    <Toast className={props.state.type == 'OK' ? 'bg-success' : 'bg-danger'}>
      <Toast.Header closeButton={false}>
        <strong className="me-auto">{props.state.type}</strong>
        {/* <small>{msgElapsed(time)}</small> */}
      </Toast.Header>
      <Toast.Body>{props.state.msg} </Toast.Body>
    </Toast>
  );
};

const StateToast = () => {
  return (
    <div className={'position-absolute toast-container bottom-0 end-0'}>
      <StatesConsumer>
        {(value) =>
          [...value.states].reverse().map((s) => {
            return <SingleState key={s.id} state={s}></SingleState>;
          })
        }
      </StatesConsumer>
    </div>
  );
};

export default StateToast;
